import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, FlaskConical } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/data/site";

const QUICK = ["What are exosomes?", "Product prices", "Shipping & payment", "Partner program"];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Welcome to Exoplus Bio. I'm the lab assistant — ask me about our exosome products, prices, payment or the partner program.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const sessionId = useRef(
    (() => {
      let id = localStorage.getItem("exoplusbio-chat-session");
      if (!id) {
        id = "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem("exoplusbio-chat-session", id);
      }
      return id;
    })()
  );

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      if (!res.ok) throw new Error();
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: acc };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Sorry — I can't respond right now. Please try again or use the contact form.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 z-[60] flex h-[520px] w-[calc(100vw-2rem)] max-w-[380px] flex-col border border-[#e4e9ec] bg-white shadow-2xl md:right-8"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between bg-[#070b0c] px-4 py-3.5 text-white">
              <div className="flex items-center gap-2.5">
                <FlaskConical className="h-4 w-4 text-[#0e8c8c]" />
                <div>
                  <p className="text-sm font-medium leading-none">Lab Assistant</p>
                  <p className="font-lab mt-1 text-[9px] uppercase tracking-[0.18em] text-white/50">
                    Exoplus Bio · FAQs
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close-button">
                <X className="h-4 w-4 text-white/70 hover:text-white" />
              </button>
            </div>

            <div ref={scrollRef} className="relative flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] px-3.5 py-2.5 text-[13px] leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto bg-[#0a0a0a] text-white"
                      : "border border-[#e4e9ec] bg-[#fafafa] text-[#0a0a0a]"
                  }`}
                  data-testid={m.role === "user" ? "chat-message-user" : "chat-message-assistant"}
                >
                  {m.text || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-1.5 px-4 pb-2">
              {QUICK.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  data-testid="chat-quick-chip"
                  className="font-lab border border-[#e4e9ec] px-2.5 py-1.5 text-[10px] text-[#56636b] transition-colors hover:border-[#0e8c8c] hover:text-[#0e8c8c]"
                >
                  {q}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 border-t border-[#e4e9ec] p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about products, orders…"
                data-testid="chat-input"
                className="flex-1 border border-[#e4e9ec] px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#0e8c8c]"
              />
              <button
                onClick={() => send()}
                disabled={busy || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Send message"
                className="flex h-10 w-10 items-center justify-center bg-[#0a0a0a] text-white transition-colors hover:bg-[#0e8c8c] disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-launcher"
        aria-label="Open chat assistant"
        className="fixed bottom-6 right-4 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-[#0a0a0a] text-white shadow-xl transition-transform duration-300 hover:scale-105 hover:bg-[#0e8c8c] md:right-8"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        {!open && <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-[#0e8c8c]" />}
      </button>
    </>
  );
};

export default ChatWidget;